import type { VehicleType } from '@/state/types';

/**
 * Phase 9.2 — Module-Familien mit Größenvarianten.
 *
 * Vorher (Phase 7.2.5) war jedes Modul ein eigener Eintrag. Jetzt sind sie in
 * Familien gruppiert (z. B. "water-tank") mit optionalen Varianten (1000l,
 * 1200l, 1800l, 2000l). Eine Modul-Anforderung im Maßnahmen-System (9.1)
 * adressiert Familie + minSize.
 *
 * Identitäts-Schema:
 *   "<family>"             — wenn keine Varianten existieren (z. B. "winch")
 *   "<family>:<variant>"   — z. B. "water-tank:1200l"
 */

export type ModuleCategory =
  | 'water'
  | 'breathing'
  | 'rescue'
  | 'special'
  | 'medical'
  | 'police';

export interface ModuleEffects {
  waterOutputBonus?: number;
  progressMultiplier?: number;
  breathingAirBonus?: number;
  /** Zusätzlicher Wassertank-Inhalt (Liter), wird beim Kauf addiert. */
  waterCapacity?: number;
  /** Zusätzlicher Atemluft-Vorrat (Liter). */
  airCapacity?: number;
  /** Multiplier auf Rettungs-/Bergungs-Geschwindigkeit. */
  rescueSpeed?: number;
  enablesPrimaryFunction?: 'dlk-ladder';
  // Phase 13.7 — Wasserentnahme aus offenen Gewässern (Flüsse, Seen, Kanäle).
  canPumpFromOpenWater?: boolean;
  /** L/min Förderleistung wenn an Gewässer angeschlossen. */
  openWaterOutput?: number;
  // Phase 10.2.1 — Polizei-Effekte
  /** Vehicle can take in arrested persons; non-zero capacity = how many. */
  arrestCapacity?: number;
  /** Police-K9 module — track + intimidation bonus. */
  canTrack?: boolean;
  intimidationBonus?: number;
  /** Crowd-control / Schildwall vorhanden. */
  crowdControl?: boolean;
  /** Tactical gear level (0..2). */
  tacticalLevel?: number;
  /** Door-breaching möglich. */
  canBreachDoor?: boolean;
  /** Verdeckte Beobachtung möglich. */
  canSurveillance?: boolean;
}

/** A single size/variant of a module family. */
export interface ModuleVariant {
  /** Size key (e.g. "small", "1200l"). Undefined for single-size families. */
  size?: string;
  /** Display label for this variant ("1.200 l", "Groß"). */
  label?: string;
  cost: number;
  /**
   * Slot count on the host vehicle. 0 means the module is built-in (e.g. the
   * FWK crane sits on its own structure and doesn't consume a generic slot).
   */
  slots: 0 | 1 | 2;
  effects: ModuleEffects;
}

export interface ModuleFamily {
  /** Stable identifier ("water-tank", "hydraulic-rescue-set"). */
  family: string;
  /** Display name shown in the UI. */
  name: string;
  category: ModuleCategory;
  description: string;
  /** One or more variants. Single-size families have one variant with `size: undefined`. */
  variants: ModuleVariant[];
  /** Vehicle types that may install this module. */
  compatibleTypes: VehicleType[];
}

/* ──────────── Catalog ──────────── */

const FIRE_PUMPERS: VehicleType[] = ['LF', 'HLF', 'TLF', 'TSF'];
const FIRE_RESCUE: VehicleType[] = ['HLF', 'LF', 'RW'];
const ALL_FIRE: VehicleType[] = [
  'LF', 'HLF', 'TLF', 'TSF', 'DLK', 'RW', 'GW', 'SW', 'ELW', 'MTW', 'PKW', 'FWK',
];

export const MODULE_FAMILIES: ModuleFamily[] = [
  {
    family: 'water-tank',
    name: 'Wassertank-Erweiterung',
    category: 'water',
    description: 'Vergrößert den fest verbauten Wasservorrat.',
    compatibleTypes: ['HLF', 'LF', 'TLF'],
    variants: [
      { size: '1000l', label: '1.000 l', cost: 45_000, slots: 1, effects: { waterCapacity: 1000 } },
      { size: '1200l', label: '1.200 l', cost: 50_000, slots: 1, effects: { waterCapacity: 1200 } },
      { size: '1800l', label: '1.800 l', cost: 55_000, slots: 2, effects: { waterCapacity: 1800 } },
      { size: '2000l', label: '2.000 l', cost: 60_000, slots: 2, effects: { waterCapacity: 2000 } },
    ],
  },
  {
    family: 'breathing-air',
    name: 'Atemschutz-Ausstattung',
    category: 'breathing',
    description: 'Atemluftflaschen + Masken für AGT-Trupps.',
    compatibleTypes: ['HLF', 'LF', 'TLF', 'GW'],
    variants: [
      { size: 'small', label: '6 Flaschen', cost: 30_000, slots: 1, effects: { airCapacity: 6_000 } },
      { size: 'medium', label: '10 Flaschen', cost: 40_000, slots: 1, effects: { airCapacity: 10_000 } },
      { size: 'large', label: '16 Flaschen', cost: 50_000, slots: 2, effects: { airCapacity: 16_000 } },
    ],
  },
  {
    family: 'hydraulic-rescue-set',
    name: 'Hydraulischer Rettungssatz',
    category: 'rescue',
    description: 'Spreizer und Schere für eingeklemmte Personen.',
    compatibleTypes: FIRE_RESCUE,
    variants: [
      { size: 'small', label: 'Klein', cost: 15_000, slots: 1, effects: { rescueSpeed: 1.0, progressMultiplier: 1.15 } },
      { size: 'large', label: 'Groß', cost: 25_000, slots: 2, effects: { rescueSpeed: 1.5, progressMultiplier: 1.4 } },
    ],
  },
  {
    family: 'winch',
    name: 'Bergungswinde',
    category: 'rescue',
    description: 'Seilwinde für Fahrzeugbergung.',
    compatibleTypes: ['RW', 'HLF'],
    variants: [{ cost: 20_000, slots: 1, effects: { progressMultiplier: 1.2 } }],
  },
  {
    family: 'technical-equipment',
    name: 'Technische Geräte',
    category: 'rescue',
    description: 'Stromerzeuger, Beleuchtung, Kleinwerkzeug.',
    compatibleTypes: ALL_FIRE,
    variants: [{ cost: 40_000, slots: 1, effects: { progressMultiplier: 1.1 } }],
  },
  {
    family: 'environmental-protection',
    name: 'Umweltschutz-Set',
    category: 'special',
    description: 'Ölbinder, Auffangbehälter, Schutzkleidung.',
    compatibleTypes: ['LF', 'HLF', 'GW', 'RW'],
    variants: [{ cost: 25_000, slots: 1, effects: {} }],
  },
  {
    family: 'foam-tank',
    name: 'Schaummittel-Tank',
    category: 'water',
    description: 'Schaummittel für Industrie- und Vegetationsbrände.',
    compatibleTypes: FIRE_PUMPERS,
    variants: [{ cost: 35_000, slots: 1, effects: { progressMultiplier: 1.15 } }],
  },
  {
    family: 'all-wheel-drive',
    name: 'Allrad-Antrieb',
    category: 'special',
    description: 'Geländegängiges Fahrwerk für Vegetation und Schnee.',
    compatibleTypes: ['LF', 'HLF', 'TLF', 'GW', 'RW'],
    variants: [{ cost: 40_000, slots: 1, effects: {} }],
  },
  {
    family: 'water-monitor',
    name: 'Wasserwerfer / Monitor',
    category: 'water',
    description: 'Werfer auf dem Dach, +200 L/min Förderleistung.',
    compatibleTypes: FIRE_PUMPERS,
    variants: [{ cost: 40_000, slots: 2, effects: { waterOutputBonus: 200 } }],
  },
  {
    family: 'hose-reel',
    name: 'Schnellangriffshaspel',
    category: 'water',
    description: 'B-Schlauch-Erweiterung für lange Hydranten-Wege.',
    compatibleTypes: ['SW', 'LF', 'HLF', 'TLF'],
    variants: [{ cost: 30_000, slots: 1, effects: {} }],
  },
  // Phase 13.7 — Wasserentnahme aus offenen Gewässern
  {
    family: 'open-water-pump',
    name: 'Saugkorb (offenes Gewässer)',
    category: 'water',
    description:
      'Saugleitung + Saugkorb für Wasserentnahme aus Flüssen, Seen oder Kanälen.',
    compatibleTypes: ['HLF', 'LF', 'TLF', 'GW', 'GWL'],
    variants: [
      {
        size: 'standard',
        label: 'Standard',
        cost: 35_000,
        slots: 1,
        effects: { canPumpFromOpenWater: true, openWaterOutput: 800 },
      },
      {
        size: 'heavy',
        label: 'Schwer',
        cost: 60_000,
        slots: 2,
        effects: { canPumpFromOpenWater: true, openWaterOutput: 1600 },
      },
    ],
  },
  {
    family: 'first-responder',
    name: 'First-Responder-Ausstattung',
    category: 'medical',
    description: 'Notfallausrüstung für Ersthelfer (ELW als ELRD).',
    compatibleTypes: ['ELW', 'PKW'],
    variants: [{ cost: 20_000, slots: 1, effects: {} }],
  },
  {
    family: 'heavy-transport',
    name: 'Tragkraft-Erweiterung',
    category: 'medical',
    description: 'Schwerlast-Trage für adipöse Patienten (RTW/KTW).',
    compatibleTypes: ['RTW', 'KTW'],
    variants: [{ cost: 60_000, slots: 1, effects: {} }],
  },
  {
    family: 'emergency-doctor-equip',
    name: 'Notarzt-Ausstattung',
    category: 'medical',
    description: 'Notarzt-Material, EKG, Beatmung.',
    compatibleTypes: ['RTW'],
    variants: [{ cost: 80_000, slots: 1, effects: {} }],
  },
  {
    family: 'emergency-care',
    name: 'Notfall-Versorgung',
    category: 'medical',
    description: 'Erweiterte Patientenversorgung (KTW-Aufrüstung).',
    compatibleTypes: ['KTW'],
    variants: [{ cost: 70_000, slots: 1, effects: {} }],
  },
  {
    family: 'crane',
    name: 'Schwerlast-Kran',
    category: 'rescue',
    description: 'Kran für Schwerlast-Hebungen — exklusiv FWK.',
    compatibleTypes: ['FWK'],
    variants: [{ cost: 0, slots: 0, effects: { progressMultiplier: 1.5 } }],
  },

  /* Phase 10.2.1 — Polizei-Module */
  {
    family: 'arrest-equipment',
    name: 'Festnahme-Ausrüstung',
    category: 'police',
    description: 'Handfesseln, Gefangenen-Trennwand. Erhöht Festnahme-Kapazität.',
    // Phase 13.3 — auch PolPKW + GefKW dürfen Festnahme-Ausrüstung tragen.
    compatibleTypes: ['FuStW', 'GruKW', 'PolPKW', 'GefKW'],
    variants: [
      { size: 'standard', label: '2 Plätze', cost: 5_000, slots: 1, effects: { arrestCapacity: 2 } },
      { size: 'extended', label: '4 Plätze', cost: 12_000, slots: 1, effects: { arrestCapacity: 4 } },
      { size: 'transport', label: '8 Plätze (GefKW)', cost: 25_000, slots: 1, effects: { arrestCapacity: 8 } },
    ],
  },
  {
    family: 'police-dog',
    name: 'Diensthund',
    category: 'police',
    description: 'Erfordert Diensthundezwinger an der Wache. Spurensuche + Einschüchterung.',
    // Phase 13.2/13.3 — auch PolPKW darf Diensthund mitführen.
    compatibleTypes: ['FuStW', 'GruKW', 'PolPKW'],
    variants: [{
      cost: 25_000,
      slots: 1,
      effects: { canTrack: true, intimidationBonus: 0.3 },
    }],
  },
  {
    family: 'riot-shield-set',
    name: 'Schutzschild-Set',
    category: 'police',
    description: 'Schutzschilde für Demos und Crowd Control.',
    compatibleTypes: ['GruKW'],
    variants: [{ cost: 30_000, slots: 1, effects: { crowdControl: true } }],
  },
  {
    family: 'tactical-gear',
    name: 'Taktische Ausrüstung',
    category: 'police',
    description: 'Ballistische Westen, MP, Nachtsicht. Pflicht für SEK-/MEK-Einsätze.',
    compatibleTypes: ['SEKWg', 'MEKWg'],
    variants: [
      { size: 'standard', label: 'Standard', cost: 80_000, slots: 1, effects: { tacticalLevel: 1 } },
      { size: 'heavy', label: 'Schwer', cost: 150_000, slots: 2, effects: { tacticalLevel: 2 } },
    ],
  },
  {
    family: 'breaching-equipment',
    name: 'Türöffnungs-Set',
    category: 'police',
    description: 'Rammen + hydraulische Spreizer für Tür-Aufbruch.',
    compatibleTypes: ['SEKWg'],
    variants: [{ cost: 45_000, slots: 1, effects: { canBreachDoor: true } }],
  },
  {
    family: 'surveillance-tech',
    name: 'Observations-Technik',
    category: 'police',
    description: 'Kameras, Richtmikrofon, Drohne. Verdeckte Aufklärung.',
    compatibleTypes: ['MEKWg', 'GruKW'],
    variants: [{ cost: 60_000, slots: 1, effects: { canSurveillance: true } }],
  },
  {
    family: 'water-cannon',
    name: 'Wasserwerfer-Aufbau',
    category: 'police',
    description: 'Hochdruck-Werfer auf dem Dach. Crowd Control.',
    compatibleTypes: ['WaWe'],
    variants: [
      { size: '6000l', label: '6.000 l', cost: 100_000, slots: 1, effects: { waterCapacity: 6_000, crowdControl: true } },
      { size: '10000l', label: '10.000 l', cost: 180_000, slots: 2, effects: { waterCapacity: 10_000, crowdControl: true } },
    ],
  },
  {
    family: 'dlk-ladder',
    name: 'Drehleiter',
    category: 'rescue',
    description: 'Drehleiter mit Korb. Pflichtmodul, ohne nicht alarmierbar.',
    compatibleTypes: ['DLK'],
    variants: [{ cost: 0, slots: 2, effects: { enablesPrimaryFunction: 'dlk-ladder' } }],
  },
];

/** Variant-size ranking for `minSize` comparisons. Higher rank = bigger. */
const SIZE_RANK: Record<string, number> = {
  // generic
  small: 1,
  medium: 2,
  large: 3,
  // water tank in liters
  '1000l': 1,
  '1200l': 2,
  '1800l': 3,
  '2000l': 4,
};

export function rankSize(size: string | undefined): number {
  if (!size) return 0;
  return SIZE_RANK[size] ?? 0;
}

/** Compose an instance moduleId from a family + variant. */
export function moduleIdOf(family: string, size?: string): string {
  return size ? `${family}:${size}` : family;
}

/** Inverse — split moduleId into family + size. */
export function splitModuleId(id: string): { family: string; size?: string } {
  const idx = id.indexOf(':');
  if (idx === -1) return { family: id };
  return { family: id.slice(0, idx), size: id.slice(idx + 1) };
}

/** Lookup a family by id. */
export function familyById(family: string): ModuleFamily | null {
  return MODULE_FAMILIES.find((f) => f.family === family) ?? null;
}

/** Lookup a specific variant. */
export function variantOf(family: string, size?: string): ModuleVariant | null {
  const fam = familyById(family);
  if (!fam) return null;
  if (!size) return fam.variants[0] ?? null;
  return fam.variants.find((v) => v.size === size) ?? null;
}

/** Lookup the (family, variant) tuple from a composite id. */
export function moduleByCompositeId(
  id: string,
): { family: ModuleFamily; variant: ModuleVariant } | null {
  const { family, size } = splitModuleId(id);
  const fam = familyById(family);
  if (!fam) return null;
  const v = size ? fam.variants.find((x) => x.size === size) : fam.variants[0];
  if (!v) return null;
  return { family: fam, variant: v };
}

/** All families (with at least one variant) compatible with the given type. */
export function familiesForType(type: VehicleType): ModuleFamily[] {
  return MODULE_FAMILIES.filter((f) => f.compatibleTypes.includes(type));
}

/** Sum of slots used by installed module composite-ids. */
export function usedSlotsForInstalls(installedIds: string[]): number {
  return installedIds.reduce((acc, id) => {
    const m = moduleByCompositeId(id);
    return acc + (m?.variant.slots ?? 0);
  }, 0);
}

/** Combined effects of all installed modules. */
export function combinedEffects(installedIds: string[]): ModuleEffects {
  const out: ModuleEffects = {};
  for (const id of installedIds) {
    const m = moduleByCompositeId(id);
    if (!m) continue;
    const e = m.variant.effects;
    if (e.waterOutputBonus !== undefined) {
      out.waterOutputBonus = (out.waterOutputBonus ?? 0) + e.waterOutputBonus;
    }
    if (e.breathingAirBonus !== undefined) {
      out.breathingAirBonus = (out.breathingAirBonus ?? 0) + e.breathingAirBonus;
    }
    if (e.waterCapacity !== undefined) {
      out.waterCapacity = (out.waterCapacity ?? 0) + e.waterCapacity;
    }
    if (e.airCapacity !== undefined) {
      out.airCapacity = (out.airCapacity ?? 0) + e.airCapacity;
    }
    if (e.progressMultiplier !== undefined) {
      out.progressMultiplier = (out.progressMultiplier ?? 1) * e.progressMultiplier;
    }
    if (e.rescueSpeed !== undefined) {
      out.rescueSpeed = Math.max(out.rescueSpeed ?? 1, e.rescueSpeed);
    }
    if (e.enablesPrimaryFunction) {
      out.enablesPrimaryFunction = e.enablesPrimaryFunction;
    }
  }
  return out;
}

/**
 * Phase 9.1.3 — does an installed module satisfy a requirement?
 *  - family must match
 *  - if `minSize` is given, the variant's rank must be ≥ requested rank
 */
export function moduleSatisfies(
  installedId: string,
  req: { family: string; minSize?: string },
): boolean {
  const { family, size } = splitModuleId(installedId);
  if (family !== req.family) return false;
  if (!req.minSize) return true;
  return rankSize(size) >= rankSize(req.minSize);
}

/* ──────────── Legacy compatibility ──────────── */

/**
 * Phase 9.2 migration table: maps old Phase-7 module IDs to new composite IDs.
 * Returns null when a clean mapping isn't possible — the v9 → v10 migration
 * deletes those modules and refunds 100 % of the old cost.
 */
const LEGACY_MAP: Record<string, { newId: string; refund: number } | null> = {
  'mod-monitor': { newId: 'water-monitor', refund: 25_000 },
  'mod-foam': { newId: 'foam-tank', refund: 18_000 },
  'mod-hose-extension': { newId: 'hose-reel', refund: 12_000 },
  'mod-as-fillstation': { newId: 'breathing-air:large', refund: 22_000 },
  'mod-mask-pool': { newId: 'breathing-air:small', refund: 9_000 },
  'mod-hydraulic-rescue': { newId: 'hydraulic-rescue-set:large', refund: 35_000 },
  'mod-airbag': { newId: 'technical-equipment', refund: 15_000 },
  'mod-winch': { newId: 'winch', refund: 28_000 },
  'mod-crane': { newId: 'crane', refund: 60_000 },
  'mod-power-generator': { newId: 'technical-equipment', refund: 14_000 },
  'mod-light-tower': { newId: 'technical-equipment', refund: 10_000 },
  'mod-pump-extension': { newId: 'water-monitor', refund: 20_000 },
  'mod-wlf-container': { newId: 'heavy-transport', refund: 45_000 },
  'mod-dlk-ladder': { newId: 'dlk-ladder', refund: 0 },
};

export function migrateLegacyModuleId(
  oldId: string,
): { newId: string; refund: number } | null {
  return LEGACY_MAP[oldId] ?? null;
}

/* ──────────── Backwards-compatible exports ──────────── */

/**
 * Legacy `moduleById` lookup — used by older code paths that still reference
 * single-id modules. Returns a flat shape compatible with Phase 7.2.5 callers.
 */
export interface LegacyModuleShape {
  id: string;
  name: string;
  category: ModuleCategory;
  cost: number;
  slots: 1 | 2;
  compatibleTypes: VehicleType[];
  description: string;
  effects: ModuleEffects;
}

export function moduleById(id: string): LegacyModuleShape | null {
  const m = moduleByCompositeId(id);
  if (!m) return null;
  // Slots fall back to 1 for legacy callers that assume 1|2 only.
  const legacySlots: 1 | 2 = m.variant.slots > 1 ? 2 : 1;
  return {
    id,
    name: m.variant.label ? `${m.family.name} (${m.variant.label})` : m.family.name,
    category: m.family.category,
    cost: m.variant.cost,
    slots: legacySlots,
    compatibleTypes: m.family.compatibleTypes,
    description: m.family.description,
    effects: m.variant.effects,
  };
}

/**
 * Returns one entry per family/variant combination, used by the install dialog.
 */
export function modulesForType(type: VehicleType): LegacyModuleShape[] {
  const out: LegacyModuleShape[] = [];
  for (const fam of MODULE_FAMILIES) {
    if (!fam.compatibleTypes.includes(type)) continue;
    for (const v of fam.variants) {
      const id = moduleIdOf(fam.family, v.size);
      const ms = moduleById(id);
      if (ms) out.push(ms);
    }
  }
  return out;
}

/** Backwards-compat constant for tests/components that imported VEHICLE_MODULES. */
export const VEHICLE_MODULES: LegacyModuleShape[] = MODULE_FAMILIES.flatMap((fam) =>
  fam.variants.map((v) => {
    const id = moduleIdOf(fam.family, v.size);
    return moduleById(id) as LegacyModuleShape;
  }),
);
